/**
 * Admin Page
 *
 * Platform administration and settings page:
 * - System overview and statistics
 * - Integrations management
 * - Platform configuration
 * - API usage monitoring
 */
import React, { useState, useEffect } from 'react';
import {
  Title1,
  Title3,
  Text,
  Card,
  CardHeader,
  CardPreview,
  Button,
  Badge,
  makeStyles,
  tokens,
  Tab,
  TabList,
  Spinner,
  type SelectTabEvent,
  type SelectTabData,
  Divider,
  Switch,
  Input,
  Field,
  Dropdown,
  Option,
  Link,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import {
  SettingsRegular,
  PlugConnectedRegular,
  ShieldCheckmarkRegular,
  ChartMultipleRegular,
  AlertRegular,
  CheckmarkCircleRegular,
  DismissCircleRegular,
  ArrowSyncRegular,
  AddRegular,
  EditRegular,
  ClockRegular,
  SaveRegular,
  ArrowResetRegular,
} from '@fluentui/react-icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dashboardApi, adminApi, settingsApi, type TenantSettingsData } from '../services/api';
import type { IntegrationStatusDto, ActivityType } from '../types';
import { UnauthenticatedBanner } from '../components/common/UnauthenticatedBanner';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXL,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  headerIcon: {
    fontSize: '32px',
    color: tokens.colorBrandForeground1,
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: tokens.spacingHorizontalL,
  },
  statCard: {
    padding: tokens.spacingVerticalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  statValue: {
    fontSize: tokens.fontSizeHero800,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorBrandForeground1,
  },
  statLabel: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  integrationGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: tokens.spacingHorizontalL,
  },
  integrationCard: {
    minHeight: '160px',
  },
  integrationHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  integrationTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  integrationIcon: {
    fontSize: '24px',
    color: tokens.colorBrandForeground1,
  },
  integrationStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  integrationActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalM,
  },
  tabContent: {
    paddingTop: tokens.spacingVerticalL,
  },
  settingsForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    maxWidth: '600px',
  },
  formSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  formRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    alignItems: 'flex-start',
  },
  recentActivity: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  activityItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  activityIcon: {
    fontSize: '20px',
  },
  activityContent: {
    flex: 1,
  },
  activityTime: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
});

type TabValue = 'overview' | 'integrations' | 'settings' | 'activity';

export const AdminPage: React.FC = () => {
  const styles = useStyles();
  const [selectedTab, setSelectedTab] = useState<TabValue>('overview');
  const [activityFilter, setActivityFilter] = useState<ActivityType | undefined>(undefined);

  // Fetch dashboard data for stats
  const { data: securityDashboard, isLoading: loadingDashboard, isError, error } = useQuery({
    queryKey: ['admin', 'security-dashboard'],
    queryFn: () => dashboardApi.getSecurityDashboard(),
  });

  // Fetch integrations summary from API
  const { data: integrationsSummary, isLoading: loadingIntegrations } = useQuery({
    queryKey: ['admin', 'integrations-summary'],
    queryFn: () => adminApi.getIntegrationsSummary(),
  });

  // Fetch activity log from API
  const { data: activityLog, isLoading: loadingActivity } = useQuery({
    queryKey: ['admin', 'activity', activityFilter],
    queryFn: () => adminApi.getActivityLog(activityFilter, 50),
  });

  // Fetch tenant settings from API
  const { data: tenant } = useQuery({
    queryKey: ['tenant', 'current'],
    queryFn: () => settingsApi.getCurrentTenant(),
  });

  const { data: settingsResponse, isLoading: loadingSettings } = useQuery({
    queryKey: ['tenant', 'settings'],
    queryFn: () => settingsApi.getTenantSettings(),
  });

  const queryClient = useQueryClient();

  // Settings form state
  const [notificationEmail, setNotificationEmail] = useState('');
  const [requireMfa, setRequireMfa] = useState(false);
  const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(true);
  const [mcpEnabled, setMcpEnabled] = useState(true);
  const [settingsSuccess, setSettingsSuccess] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);

  // Initialize settings form from API
  useEffect(() => {
    if (settingsResponse?.settings) {
      const s = settingsResponse.settings;
      setNotificationEmail(s.notificationEmail || '');
      setRequireMfa(s.requireMfa);
      setEmailNotificationsEnabled(s.emailNotificationsEnabled);
      setMcpEnabled(s.mcpEnabled);
    }
  }, [settingsResponse]);

  // Settings mutation
  const saveSettingsMutation = useMutation({
    mutationFn: (settings: Partial<TenantSettingsData>) => settingsApi.updateTenantSettings(settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant'] });
      setSettingsSuccess(true);
      setSettingsError(null);
      setTimeout(() => setSettingsSuccess(false), 3000);
    },
    onError: (error: Error) => {
      setSettingsError(error.message);
      setTimeout(() => setSettingsError(null), 5000);
    },
  });

  const handleSaveSettings = () => {
    saveSettingsMutation.mutate({
      notificationEmail,
      requireMfa,
      emailNotificationsEnabled,
      mcpEnabled,
    });
  };

  const handleResetSettings = () => {
    if (settingsResponse?.settings) {
      const s = settingsResponse.settings;
      setNotificationEmail(s.notificationEmail || '');
      setRequireMfa(s.requireMfa);
      setEmailNotificationsEnabled(s.emailNotificationsEnabled);
      setMcpEnabled(s.mcpEnabled);
    }
  };

  const integrations = integrationsSummary?.integrations ?? [];
  const recentActivity = activityLog ?? [];

  const handleTabSelect = (_event: SelectTabEvent, data: SelectTabData) => {
    setSelectedTab(data.value as TabValue);
  };

  const getStatusBadge = (status: IntegrationStatusDto['status']) => {
    switch (status) {
      case 'connected':
        return <Badge appearance="filled" color="success" icon={<CheckmarkCircleRegular />}>Connected</Badge>;
      case 'configured':
        return <Badge appearance="filled" color="informative" icon={<ClockRegular />}>Configured</Badge>;
      case 'error':
        return <Badge appearance="filled" color="danger" icon={<DismissCircleRegular />}>Error</Badge>;
      default:
        return <Badge appearance="outline" color="subtle">Not Configured</Badge>;
    }
  };

  const renderOverview = () => (
    <div className={styles.section}>
      {/* Stats Cards */}
      <div className={styles.statsGrid}>
        <Card className={styles.statCard}>
          <div className={styles.statValue}>{securityDashboard?.totalFindings ?? 0}</div>
          <Text className={styles.statLabel}>Total Findings</Text>
        </Card>
        <Card className={styles.statCard}>
          <div className={styles.statValue}>{securityDashboard?.criticalFindings ?? 0}</div>
          <Text className={styles.statLabel}>Critical Issues</Text>
        </Card>
        <Card className={styles.statCard}>
          <div className={styles.statValue}>{integrationsSummary?.totalConnected ?? 0}</div>
          <Text className={styles.statLabel}>Active Integrations</Text>
        </Card>
        <Card className={styles.statCard}>
          <div className={styles.statValue}>{securityDashboard?.riskScore ?? 0}</div>
          <Text className={styles.statLabel}>Risk Score</Text>
        </Card>
      </div>

      <Divider />

      {/* Quick Actions */}
      <div className={styles.sectionHeader}>
        <Title3>Quick Actions</Title3>
      </div>
      <div className={styles.integrationGrid}>
        <Card>
          <CardHeader
            header={<Text weight="semibold">Trigger Security Scan</Text>}
            description="Run a full security assessment"
            action={<Button icon={<ShieldCheckmarkRegular />}>Run Scan</Button>}
          />
        </Card>
        <Card>
          <CardHeader
            header={<Text weight="semibold">Sync Integrations</Text>}
            description="Refresh data from connected services"
            action={<Button icon={<ArrowSyncRegular />}>Sync All</Button>}
          />
        </Card>
        <Card>
          <CardHeader
            header={<Text weight="semibold">View Reports</Text>}
            description="Generate enforcement and security reports"
            action={<Button icon={<ChartMultipleRegular />}>Reports</Button>}
          />
        </Card>
      </div>

      <Divider />

      {/* Recent Activity */}
      <div className={styles.sectionHeader}>
        <Title3>Recent Activity</Title3>
        <Link>View All</Link>
      </div>
      <div className={styles.recentActivity}>
        {loadingActivity ? (
          <Spinner size="small" label="Loading activity..." />
        ) : recentActivity.length === 0 ? (
          <Text>Activity log populates as users interact with the platform.</Text>
        ) : (
          recentActivity.slice(0, 5).map((activity) => (
            <div key={activity.id} className={styles.activityItem}>
              {activity.success ? (
                <CheckmarkCircleRegular className={styles.activityIcon} style={{ color: tokens.colorPaletteGreenForeground1 }} />
              ) : (
                <AlertRegular className={styles.activityIcon} style={{ color: tokens.colorPaletteRedForeground1 }} />
              )}
              <div className={styles.activityContent}>
                <Text>{activity.message}</Text>
              </div>
              <Text className={styles.activityTime}>{activity.time}</Text>
            </div>
          ))
        )}
      </div>
    </div>
  );

  const renderIntegrations = () => {
    if (loadingIntegrations) {
      return (
        <div className={styles.section}>
          <Spinner size="small" label="Loading integrations..." />
        </div>
      );
    }

    return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <Title3>Connected Integrations</Title3>
        <Button appearance="primary" icon={<AddRegular />}>Add Integration</Button>
      </div>

      {/* SCM Integrations */}
      <Title3>Source Code Management</Title3>
      <div className={styles.integrationGrid}>
        {integrations.filter(i => i.type === 'scm').map((integration) => (
          <Card key={integration.id} className={styles.integrationCard}>
            <CardHeader
              header={
                <div className={styles.integrationHeader}>
                  <div className={styles.integrationTitle}>
                    <PlugConnectedRegular className={styles.integrationIcon} />
                    <Text weight="semibold">{integration.name}</Text>
                  </div>
                  {getStatusBadge(integration.status)}
                </div>
              }
              description={integration.description}
            />
            <CardPreview>
              <div style={{ padding: tokens.spacingHorizontalM }}>
                {integration.status === 'connected' && (
                  <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                    {integration.itemCount} projects synced
                    {integration.lastSyncAt && ` - Last sync: ${new Date(integration.lastSyncAt).toLocaleString()}`}
                  </Text>
                )}
                <div className={styles.integrationActions}>
                  {integration.status === 'connected' ? (
                    <>
                      <Button appearance="outline" size="small" icon={<ArrowSyncRegular />}>Sync</Button>
                      <Button appearance="outline" size="small" icon={<EditRegular />}>Configure</Button>
                    </>
                  ) : (
                    <Button appearance="primary" size="small" icon={<AddRegular />}>Connect</Button>
                  )}
                </div>
              </div>
            </CardPreview>
          </Card>
        ))}
      </div>

      {/* Ticketing Integrations */}
      <Title3>Issue Tracking</Title3>
      <div className={styles.integrationGrid}>
        {integrations.filter(i => i.type === 'ticketing').map((integration) => (
          <Card key={integration.id} className={styles.integrationCard}>
            <CardHeader
              header={
                <div className={styles.integrationHeader}>
                  <div className={styles.integrationTitle}>
                    <PlugConnectedRegular className={styles.integrationIcon} />
                    <Text weight="semibold">{integration.name}</Text>
                  </div>
                  {getStatusBadge(integration.status)}
                </div>
              }
              description={integration.description}
            />
            <CardPreview>
              <div style={{ padding: tokens.spacingHorizontalM }}>
                {integration.status === 'connected' && integration.itemCount > 0 && (
                  <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                    {integration.itemCount} issues linked
                    {integration.lastSyncAt && ` - Last tested: ${new Date(integration.lastSyncAt).toLocaleString()}`}
                  </Text>
                )}
                <div className={styles.integrationActions}>
                  {integration.status !== 'not_configured' ? (
                    <>
                      <Button appearance="outline" size="small" icon={<ArrowSyncRegular />}>Test</Button>
                      <Button appearance="outline" size="small" icon={<EditRegular />}>Configure</Button>
                    </>
                  ) : (
                    <Button appearance="primary" size="small" icon={<AddRegular />}>Connect</Button>
                  )}
                </div>
              </div>
            </CardPreview>
          </Card>
        ))}
      </div>

      {/* Notification Integrations */}
      <Title3>Notifications</Title3>
      <div className={styles.integrationGrid}>
        {integrations.filter(i => i.type === 'notification').map((integration) => (
          <Card key={integration.id} className={styles.integrationCard}>
            <CardHeader
              header={
                <div className={styles.integrationHeader}>
                  <div className={styles.integrationTitle}>
                    <AlertRegular className={styles.integrationIcon} />
                    <Text weight="semibold">{integration.name}</Text>
                  </div>
                  {getStatusBadge(integration.status)}
                </div>
              }
              description={integration.description}
            />
            <CardPreview>
              <div style={{ padding: tokens.spacingHorizontalM }}>
                {integration.status === 'connected' && integration.itemCount > 0 && (
                  <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                    {integration.itemCount} channel{integration.itemCount !== 1 ? 's' : ''} configured
                    {integration.lastSyncAt && ` - Last tested: ${new Date(integration.lastSyncAt).toLocaleString()}`}
                  </Text>
                )}
                <div className={styles.integrationActions}>
                  {integration.status === 'connected' ? (
                    <>
                      <Button appearance="outline" size="small">Test Message</Button>
                      <Button appearance="outline" size="small" icon={<EditRegular />}>Configure</Button>
                    </>
                  ) : (
                    <Button appearance="primary" size="small" icon={<AddRegular />}>Connect</Button>
                  )}
                </div>
              </div>
            </CardPreview>
          </Card>
        ))}
      </div>
    </div>
    );
  };

  const renderSettings = () => {
    if (loadingSettings) {
      return (
        <div className={styles.section}>
          <Spinner size="small" label="Loading settings..." />
        </div>
      );
    }

    return (
      <div className={styles.section}>
        {settingsSuccess && (
          <MessageBar intent="success">
            <MessageBarBody>Settings saved successfully!</MessageBarBody>
          </MessageBar>
        )}
        {settingsError && (
          <MessageBar intent="error">
            <MessageBarBody>Error saving settings: {settingsError}</MessageBarBody>
          </MessageBar>
        )}

        <div className={styles.settingsForm}>
          <div className={styles.formSection}>
            <Title3>General Settings</Title3>
            <Field label="Organization Name">
              <Input value={tenant?.name || ''} disabled />
            </Field>
            <Field label="Subscription Tier">
              <Input value={tenant?.subscription || 'Unknown'} disabled />
            </Field>
            <Field label="Notification Email" hint="Email for security alerts and notifications">
              <Input
                type="email"
                value={notificationEmail}
                onChange={(_, data) => setNotificationEmail(data.value)}
                placeholder="security@company.com"
              />
            </Field>
          </div>

          <Divider />

          <div className={styles.formSection}>
            <Title3>Security Settings</Title3>
            <Field label="Require MFA for all users">
              <Switch
                checked={requireMfa}
                onChange={(_, data) => setRequireMfa(data.checked)}
              />
            </Field>
            <Field label="Enable MCP (AI Assistant)">
              <Switch
                checked={mcpEnabled}
                onChange={(_, data) => setMcpEnabled(data.checked)}
              />
            </Field>
          </div>

          <Divider />

          <div className={styles.formSection}>
            <Title3>Notification Preferences</Title3>
            <Field label="Enable email notifications">
              <Switch
                checked={emailNotificationsEnabled}
                onChange={(_, data) => setEmailNotificationsEnabled(data.checked)}
              />
            </Field>
          </div>

          <div className={styles.formRow}>
            <Button
              appearance="primary"
              icon={<SaveRegular />}
              onClick={handleSaveSettings}
              disabled={saveSettingsMutation.isPending}
            >
              {saveSettingsMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button
              appearance="outline"
              icon={<ArrowResetRegular />}
              onClick={handleResetSettings}
              disabled={saveSettingsMutation.isPending}
            >
              Reset
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const handleActivityFilterChange = (_event: unknown, data: { optionValue?: string }) => {
    const value = data.optionValue;
    if (value === 'all' || !value) {
      setActivityFilter(undefined);
    } else {
      setActivityFilter(value as ActivityType);
    }
  };

  const renderActivity = () => (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <Title3>Activity Log</Title3>
        <div className={styles.formRow}>
          <Dropdown
            placeholder="Filter by type"
            onOptionSelect={handleActivityFilterChange}
          >
            <Option value="all">All Activities</Option>
            <Option value="scan">Scans</Option>
            <Option value="integration">Integrations</Option>
            <Option value="user">Users</Option>
            <Option value="alert">Alerts</Option>
            <Option value="authentication">Authentication</Option>
            <Option value="settings">Settings</Option>
          </Dropdown>
          <Button appearance="outline">Export</Button>
        </div>
      </div>

      <div className={styles.recentActivity}>
        {loadingActivity ? (
          <Spinner size="small" label="Loading activity..." />
        ) : recentActivity.length === 0 ? (
          <Text>No activity matching the selected filter.</Text>
        ) : (
          recentActivity.map((activity) => (
            <div key={activity.id} className={styles.activityItem}>
              {activity.success ? (
                <CheckmarkCircleRegular className={styles.activityIcon} style={{ color: tokens.colorPaletteGreenForeground1 }} />
              ) : (
                <AlertRegular className={styles.activityIcon} style={{ color: tokens.colorPaletteRedForeground1 }} />
              )}
              <div className={styles.activityContent}>
                <Text>{activity.message}</Text>
                {activity.userDisplayName && (
                  <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                    {' '}by {activity.userDisplayName}
                  </Text>
                )}
              </div>
              <Text className={styles.activityTime}>{activity.time}</Text>
            </div>
          ))
        )}
      </div>
    </div>
  );

  if (loadingDashboard && loadingIntegrations) {
    return (
      <div className={styles.page}>
        <Spinner label="Loading admin dashboard..." />
      </div>
    );
  }

  const queryError = error as any;
  const isUnauth = queryError?.status === 401 || queryError?.message?.includes('MSAL');
  if (isError && isUnauth) {
    return (
      <div className={styles.page}>
        <Title1>Administration</Title1>
        <UnauthenticatedBanner feature="administration" />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Page Header */}
      <div className={styles.header}>
        <SettingsRegular className={styles.headerIcon} />
        <div>
          <Title1>Administration</Title1>
          <Text>Manage platform settings, integrations, and security configuration</Text>
        </div>
      </div>

      {/* Tab Navigation */}
      <TabList selectedValue={selectedTab} onTabSelect={handleTabSelect}>
        <Tab value="overview" icon={<ChartMultipleRegular />}>Overview</Tab>
        <Tab value="integrations" icon={<PlugConnectedRegular />}>Integrations</Tab>
        <Tab value="settings" icon={<SettingsRegular />}>Settings</Tab>
        <Tab value="activity" icon={<ClockRegular />}>Activity</Tab>
      </TabList>

      {/* Tab Content */}
      <div className={styles.tabContent}>
        {selectedTab === 'overview' && renderOverview()}
        {selectedTab === 'integrations' && renderIntegrations()}
        {selectedTab === 'settings' && renderSettings()}
        {selectedTab === 'activity' && renderActivity()}
      </div>
    </div>
  );
};
